import { clientApi, globalApi, refreshClient } from "@/utils/api";

import { TGetMeResDto } from "@/api/services/admin/auth/dtos/get-me.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useQuery } from "@tanstack/react-query";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/admin/auth/dtos/refresh-token.dto";
import { setToken } from "@/utils/token";
import { TLogoutResDto } from "@/api/services/admin/auth/dtos/logout.dto";
import { TVerifyTokenResDto } from "@/api/services/admin/auth/dtos/verify-token.dto";

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

export const useMe = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
  });
};

// Shared refresh promise to prevent concurrent refresh calls (race condition between interceptor, withAuth, SocketProvider)
let refreshPromise: Promise<TRefreshTokenResDto> | null = null;

// Use refreshClient (no auth header, no interceptors) to avoid sending expired token
export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  // Reuse in-flight refresh request to avoid race conditions
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const rs = await refreshClient.post<TRefreshTokenResDto>("/refresh-token", payload);
      const { data } = rs.data;
      // Cognito REFRESH_TOKEN_AUTH may not return a new refreshToken — keep the existing one
      setToken(data.accessToken, data.refreshToken || payload.refreshToken);
      return rs.data;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const logoutApi = async (): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout");
  return rs?.data;
};

export const verifyTokenApi = async (token: string): Promise<boolean> => {
  const {
    data: { data },
  } = await globalApi.post<TVerifyTokenResDto>("/verify-token", { token });
  return data;
};
